// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { ActionIcon, Button, Group, Loader, Modal, Paper, Stack, Table, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type { CodeableConcept, Condition, Patient } from '@medplum/fhirtypes';
import { CodeableConceptInput, useMedplum, useMedplumProfile } from '@medplum/react';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import type { JSX } from 'react';
import { useState } from 'react';
import { ICD10_VALUESET } from '../../../constants/medical-info';
import { useRole } from '../../../hooks/useRole';
import { hasPermission } from '../../../utils/permissions';
import { withTenantScope } from '../../../utils/tenant';

function label(condition: Condition): string {
  return condition.code?.text ?? condition.code?.coding?.[0]?.display ?? condition.code?.coding?.[0]?.code ?? '—';
}

interface ConditionsSectionProps {
  patient: Patient;
  conditions: Condition[];
  loading: boolean;
  onChanged?: (conditions: Condition[]) => void;
  onRefresh: () => void;
}

export function ConditionsSection({
  patient,
  conditions,
  loading,
  onChanged,
  onRefresh,
}: ConditionsSectionProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { role, isAdmin } = useRole();
  const canEdit = hasPermission({ role, permission: 'edit-patient-profile', isAdmin });
  const [addOpen, setAddOpen] = useState(false);
  const [code, setCode] = useState<CodeableConcept | undefined>();
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string>();

  const reset = (): void => setCode(undefined);
  const addCondition = async (): Promise<void> => {
    if (!code) {
      notifications.show({ color: 'red', message: 'Please select a condition' });
      return;
    }
    setSaving(true);
    try {
      const condition: Condition = {
        resourceType: 'Condition',
        subject: createReference(patient),
        code,
        clinicalStatus: {
          coding: [{ system: 'http://terminology.hl7.org/CodeSystem/condition-clinical', code: 'active' }],
        },
        recordedDate: dayjs().format('YYYY-MM-DD'),
        recorder: profile ? createReference(profile) : undefined,
      };
      const created = await medplum.createResource<Condition>(withTenantScope(condition));
      onChanged?.([created, ...conditions]);
      onRefresh();
      setAddOpen(false);
      reset();
      notifications.show({ color: 'green', message: 'Condition added' });
    } catch (err) {
      notifications.show({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setSaving(false);
    }
  };

  const deleteCondition = async (condition: Condition): Promise<void> => {
    if (!condition.id) {
      return;
    }
    setDeletingId(condition.id);
    try {
      await medplum.deleteResource('Condition', condition.id);
      onChanged?.(conditions.filter((item) => item.id !== condition.id));
      onRefresh();
      notifications.show({ color: 'green', message: 'Condition removed' });
    } catch (err) {
      notifications.show({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setDeletingId(undefined);
    }
  };

  let content: JSX.Element;
  if (loading) {
    content = <Loader size="sm" />;
  } else if (conditions.length === 0) {
    content = <Text c="dimmed">No conditions documented.</Text>;
  } else {
    content = (
      <Table withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Condition</Table.Th>
            <Table.Th>Recorded</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {conditions.map((condition) => (
            <Table.Tr key={condition.id}>
              <Table.Td>{label(condition)}</Table.Td>
              <Table.Td>{condition.recordedDate ?? condition.onsetDateTime ?? '—'}</Table.Td>
              <Table.Td>{condition.clinicalStatus?.coding?.[0]?.code ?? 'active'}</Table.Td>
              <Table.Td>
                <ActionIcon
                  color="red"
                  variant="subtle"
                  aria-label={`Remove condition ${label(condition)}`}
                  disabled={!canEdit || deletingId === condition.id}
                  loading={deletingId === condition.id}
                  onClick={() => deleteCondition(condition)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  }

  return (
    <Paper withBorder radius="md" shadow="sm" p="xl">
      <Group justify="space-between" mb="md">
        <Title order={2}>Conditions</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setAddOpen(true)} disabled={!canEdit}>
          Add Condition
        </Button>
      </Group>
      {content}
      <Modal
        opened={addOpen}
        onClose={() => {
          setAddOpen(false);
          reset();
        }}
        title="Add Condition"
        centered
      >
        <Stack>
          <CodeableConceptInput
            binding={ICD10_VALUESET}
            label="Condition"
            name="medical-info-condition"
            path="Condition.code"
            placeholder="Search ICD-10 codes…"
            onChange={setCode}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setAddOpen(false);
                reset();
              }}
            >
              Cancel
            </Button>
            <Button onClick={addCondition} loading={saving} disabled={!code}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
